import {
  ApiBlueprint,
  createApiRef,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/frontend-plugin-api';
import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { OidcAuthApi, oidcAuthApiRef } from './oidcApi';

export const ACCESS_TOKEN_HEADER = 'X-Kovi-Access-Token';

export type AccessApp = {
  key: string;
  title: string;
  squad: string;
  folder?: string;
  runtime: 'retool_embed' | 'native';
  url?: string;
};

export type AccessMe = {
  sub: string;
  email: string;
  name: string;
  roles: string[];
  groups: string[];
  apps: string[];
  client?: string;
};

export type EmbedSession = {
  embedUrl: string;
  expiresInMinutes: number;
  mock: boolean;
};

export class AccessApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly path: string,
  ) {
    super(message);
    this.name = 'AccessApiError';
  }
}

export interface AccessApi {
  me(): Promise<AccessMe>;
  apps(): Promise<AccessApp[]>;
  embed(appKey: string): Promise<EmbedSession>;
}

export const accessApiRef = createApiRef<AccessApi>({
  id: 'kovi.access',
});

export class AccessClient implements AccessApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
    private readonly oidcApi: OidcAuthApi,
  ) {}

  async me(): Promise<AccessMe> {
    return this.request('/v1/me');
  }

  async apps(): Promise<AccessApp[]> {
    return this.request('/v1/apps');
  }

  async embed(appKey: string): Promise<EmbedSession> {
    return this.request(`/v1/embed/retool/${encodeURIComponent(appKey)}`, {
      method: 'POST',
    });
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('kovi-access');
    const token = await this.oidcApi.getAccessToken();
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        ...(init?.headers ?? {}),
        [ACCESS_TOKEN_HEADER]: token,
        Accept: 'application/json',
      },
    });
    if (!response.ok) {
      throw new AccessApiError(
        `Kovi Access API respondeu ${response.status} em ${path}`,
        response.status,
        path,
      );
    }
    return (await response.json()) as T;
  }
}

export const accessApi = ApiBlueprint.make({
  name: 'kovi-access',
  params: defineParams =>
    defineParams({
      api: accessApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
        oidcApi: oidcAuthApiRef,
      },
      factory: ({ discoveryApi, fetchApi, oidcApi }) =>
        new AccessClient(discoveryApi, fetchApi, oidcApi),
    }),
});
